import * as Api from '@ViewModels';
import moment from 'moment-timezone';

const isActionableSentiment = (item: Api.IEmailExecSentimentItem) =>
	item.sentiment === 'AtRisk' || item.sentiment === 'Negative';

// Mirrors the digest email's subject-line formula, so the card shows the same "N to review" number
// the user sees in their inbox: at-risk/negative sentiments + action items + draft replies + watchlist.
export const briefReviewCount = (digest: Api.IEmailExecDigest): number =>
	(digest.clientSentiments ?? []).filter(isActionableSentiment).length +
	(digest.proposedActionItems?.length ?? 0) +
	(digest.draftReplies?.length ?? 0) +
	(digest.watchlist?.length ?? 0);

export const isRecentBrief = (runDate: string, timeZone: string, now: Date = new Date()): boolean =>
	moment(runDate).tz(timeZone).isSameOrAfter(moment(now).tz(timeZone).subtract(1, 'day').startOf('day'));

const UNKNOWN_CONTACT = 'Unknown contact';

const reviewItem = (item: {
	detail?: string;
	kind: 'action' | 'awaiting' | 'draft' | 'risk';
	label: string;
	meta?: string;
	name?: string;
}) => ({ ...item, name: item.name || UNKNOWN_CONTACT });

export const briefReviewItems = (digest: Api.IEmailExecDigest) => [
	...(digest.draftReplies ?? []).map(draft =>
		reviewItem({
			detail: draft.subject || draft.heading,
			kind: 'draft',
			label: 'Draft ready',
			name: draft.contactName || draft.toEmail,
		})
	),
	...(digest.watchlist ?? []).map(item =>
		reviewItem({
			detail: item.originalAsk,
			kind: 'awaiting',
			label: 'Awaiting reply',
			meta: item.daysWaiting ? `${item.daysWaiting} business ${item.daysWaiting === 1 ? 'day' : 'days'}` : undefined,
			name: item.contactName || item.senderEmail,
		})
	),
	...(digest.clientSentiments ?? []).filter(isActionableSentiment).map(item =>
		reviewItem({
			detail: item.summary,
			kind: 'risk',
			label: item.sentiment === 'AtRisk' ? 'At risk' : 'Negative',
			name: item.contactName || item.senderEmail,
		})
	),
	...(digest.proposedActionItems ?? []).map(item =>
		reviewItem({
			kind: 'action',
			detail: item.text,
			label: 'Action item',
			name: item.contactName || item.senderEmail,
		})
	),
];

const stripInlineMarkdown = (line: string): string =>
	line
		.replace(/^([-*+]|\d+\.)\s+/, '')
		.replace(/^>\s*/, '')
		.replace(/!\[([^\]]*)\]\([^)]*\)/g, '$1')
		.replace(/\[([^\]]+)\]\([^)]*\)/g, '$1')
		.replace(/(\*\*|__)(.*?)\1/g, '$2')
		.replace(/(\*|_)(.*?)\1/g, '$2')
		.replace(/`([^`]*)`/g, '$1')
		.trim();

/** First meaningful line of the brief flattened to plain text; headings and rules are skipped. */
export const briefExcerpt = (markdown: string): string => {
	for (const rawLine of markdown.replace(/\r\n/g, '\n').split('\n')) {
		const line = rawLine.trim();
		if (!line || /^#{1,6}\s/.test(line) || /^([-_*]\s*){3,}$/.test(line)) {
			continue;
		}
		return stripInlineMarkdown(line);
	}
	return '';
};
